import type { MessageChannel } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type { ConversationThread } from "@redotech/effect-api-shared/models/shopper-ai/conversation/thread";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Fragment, memo, useCallback, useState } from "react";
import { useChatThreadView } from "../hooks/use-chat-thread-view";
import { useThreadMessages } from "../hooks/use-conversation-data";
import { ChatMessageBubble } from "./chat-message-bubble";
import { MessageInput } from "./message-input";
import { ThreadHeader } from "./thread-header";

interface ChatThreadViewProps {
  thread: ConversationThread;
  channel: MessageChannel;
}

export const ChatThreadView = memo(function ChatThreadView({
  thread,
  channel,
}: ChatThreadViewProps) {
  const { data: messages } = useThreadMessages(thread.id);
  const { messageGroups, lastMessageId } = useChatThreadView(messages);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  return (
    <Flex dir="column" gap="none">
      <ThreadHeader
        channel={channel}
        isCollapsed={isCollapsed}
        onClick={toggleCollapsed}
        subject={null}
      />
      {!isCollapsed && (
        <Flex
          bgColor="primary"
          borderBottomWidth="1px"
          borderColor="primary"
          borderLeftWidth="1px"
          borderRightWidth="1px"
          borderStyle="solid"
          bottomLeftRadius="sm"
          bottomRightRadius="sm"
          dir="column"
          gap="none"
        >
          <Flex dir="column" gap="lg" overflowY="auto" p="xl">
            {messageGroups.length === 0 ? (
              <Flex align="center" grow={1} justify="center" p="xl">
                <Text fontSize="md" textColor="secondary">
                  No messages in this thread
                </Text>
              </Flex>
            ) : (
              messageGroups.map((group) => (
                <Fragment key={group.dateLabel}>
                  <DateDivider label={group.dateLabel} />
                  {group.messages.map((message) => (
                    <ChatMessageBubble key={message.id} message={message} />
                  ))}
                </Fragment>
              ))
            )}
          </Flex>
          <Flex
            borderColor="secondary"
            borderStyle="solid"
            borderTopWidth="1px"
            p="xl"
          >
            <MessageInput
              conversationId={thread.conversationId}
              inReplyToMessageId={lastMessageId ?? undefined}
              mode="chat"
              threadId={thread.id}
            />
          </Flex>
        </Flex>
      )}
    </Flex>
  );
});

const DateDivider = memo(function DateDivider({ label }: { label: string }) {
  return (
    <Flex align="center" gap="md" w="full">
      <Flex
        borderBottomWidth="1px"
        borderColor="secondary"
        borderStyle="solid"
        grow={1}
      />
      <Text fontSize="xs" textColor="tertiary" whiteSpace="nowrap">
        {label}
      </Text>
      <Flex
        borderBottomWidth="1px"
        borderColor="secondary"
        borderStyle="solid"
        grow={1}
      />
    </Flex>
  );
});
